import { createColumnHelper } from "@tanstack/react-table";

import {
  EditUncuratedJobRowAction,
  RecruiterJobReportApprovedColumn,
  RecruiterJobReportInterestedColumn,
  RecruiterJobReportShortlistedColumn,
} from "./DataTable";
import { formatStartDate } from "../../helpers/strings";
import __ from "../../helpers/translations"
import DeleteRecruiterButton from "./DeleteRecruiterButton";

const columnHelper = createColumnHelper();

// Missing or unparseable dates have no position on the timeline, so they are
// treated as older than any real date. An explicit finite check keeps that
// fallback distinct from a genuine timestamp of 0 (the Unix epoch).
const parseTime = (value) => {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
};

// Sort on the parsed date rather than the raw string. Missing dates sort as oldest.
export const sortByDate = (rowA, rowB, columnId) => {
  const a = parseTime(rowA.getValue(columnId));
  const b = parseTime(rowB.getValue(columnId));
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

// Responsive priority for the recruiter tables. 1 stays on screen at any
// width; higher numbers fold into the expandable row first as the table
// narrows. See `meta.priority` in components/ui/data-table.jsx.
//
// Whatever names the row (job title, client name, a person's name) is
// IDENTIFYING, and so is any row action, which has to stay reachable at every
// width. Counts, IDs and long free text fold away first.
export const COLUMN_PRIORITY = {
  IDENTIFYING: 1, // Job Title, Client, a person's name, row actions
  IMPORTANT: 2, // Start Date, Posted, Email
  SECONDARY: 3, // Bullhorn ID, Position
  OPTIONAL: 4, // Location, Locations and the pipeline counts
};

// Shared by the job tables so "Posted" behaves the same everywhere. It leads
// both job tables: how recently a job was posted is what recruiters scan these
// lists by. This is only the default order - the Columns menu lets each
// recruiter move it, and their choice is remembered per table.
const postedColumn = columnHelper.accessor("createdAt", {
  id: "createdAt",
  cell: (info) => formatStartDate(info.getValue()),
  header: "Posted",
  sortingFn: sortByDate,
  sortDescFirst: true, // newest first on the first click
  meta: { priority: COLUMN_PRIORITY.IMPORTANT },
});

// Who to talk to about a job: the agency-side contact when there is one,
// otherwise the client's. Shared by the job tables, next to the contact-email
// filter on those pages.
const contactColumn = columnHelper.accessor(
  (row) => row.agencyContactEmail || row.employerContactEmail || "",
  {
    id: "contactEmail",
    cell: (info) => info.getValue(),
    header: "Contact",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }
);

export const jobsColumns = [
  postedColumn,
  columnHelper.accessor((row) => ({ startDate: row.startDate, isAsap: row.isAsap }), {
    id: "startDate",
    cell: (info) => {
      const { startDate, isAsap } = info.getValue();
      if (isAsap) return "ASAP";
      return formatStartDate(startDate);
    },
    header: "Start Date",
    meta: { priority: COLUMN_PRIORITY.IMPORTANT },
  }),
  columnHelper.accessor("bullhornId", {
    cell: (info) => {
      const value = info.getValue();
      if (value && value.length > 10) {
        return `${value.slice(0, 10)}...`;
      }
      return value;
    },
    header: "Bullhorn ID",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }),
  columnHelper.accessor("category", {
    cell: (info) => info.getValue(),
    header: "Job Title",
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("title", {
    cell: (info) => info.getValue(),
    header: "Position",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }),

  columnHelper.accessor("employerName", {
    cell: (info) => info.getValue(),
    header: "Client",
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  contactColumn,

  columnHelper.accessor("location", {
    cell: (info) => info.getValue(),
    header: "Location",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
];

export const activeJobsColumns = [
  postedColumn,
  columnHelper.accessor((row) => ({ startDate: row.startDate, isAsap: row.isAsap }), {
    id: "startDate",
    cell: (info) => {
      const { startDate, isAsap } = info.getValue();
      if (isAsap) return "ASAP";
      return formatStartDate(startDate);
    },
    header: "Start Date",
    meta: { priority: COLUMN_PRIORITY.IMPORTANT },
  }),
  columnHelper.accessor("bullhornId", {
    cell: (info) => {
      const value = info.getValue();
      if (value && value.length > 10) {
        return `${value.slice(0, 10)}...`;
      }
      return value;
    },
    header: "Bullhorn ID",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }),
  columnHelper.accessor("category", {
    cell: (info) => info.getValue(),
    header: "Job Title",
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("employerName", {
    cell: (info) => info.getValue(),
    header: "Client",
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("recruiterJobReport.interestedCount", {
    id: "interestedCount",
    cell: (props) => <RecruiterJobReportInterestedColumn row={props.row} />,
    header: "Interested",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
  columnHelper.accessor("recruiterJobReport.shortlistedCount", {
    id: "shortlistedCount",
    cell: (props) => <RecruiterJobReportShortlistedColumn row={props.row} />,
    header: "Shortlisted",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
  columnHelper.accessor("recruiterJobReport.approvedByClientCount", {
    id: "approvedByClientCount",
    cell: (props) => <RecruiterJobReportApprovedColumn row={props.row} />,
    header: "Approved",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
  contactColumn,
  columnHelper.accessor("location", {
    cell: (info) => info.getValue(),
    header: "Location",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
];

export const uncuratedJobsColumns = (refetch, agencyType) => [
  columnHelper.accessor("startDate", {
    cell: (info) => info.getValue(),
    header: "Start Date",
    meta: { priority: COLUMN_PRIORITY.IMPORTANT },
  }),
  columnHelper.accessor("bullhornId", {
    cell: (info) => {
      const value = info.getValue();
      if (value && value.length > 10) {
        return `${value.slice(0, 10)}...`;
      }
      return value;
    },
    header: "Bullhorn ID",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }),
  columnHelper.accessor("category", {
    cell: (info) => info.getValue(),
    header: "Job Title",
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("title", {
    cell: (info) => info.getValue(),
    header: "Position",
    meta: { priority: COLUMN_PRIORITY.SECONDARY },
  }),

  columnHelper.accessor("client", {
    cell: (info) => info.getValue(),
    header: __("Uncurated", agencyType),
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),

  columnHelper.accessor("region", {
    cell: (info) => info.getValue(),
    header: "Location",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),

  // The curate/archive menu has to stay reachable at every width.
  columnHelper.display({
    id: "actions",
    cell: (props) => (
      <EditUncuratedJobRowAction row={props.row} refetch={refetch} />
    ),
    size: 1,
    enableGlobalFilter: false,
    enableSorting: false,
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
];

export const settingsColumns = (refetch) => [
  columnHelper.accessor("name", {
    cell: (info) => info.getValue(),
    header: "Name",
  }),

  columnHelper.accessor("slugId", {
    cell: (info) => info.getValue(),
    header: "ID",
  }),

];

export const clientOrgColumns = [
  columnHelper.accessor("name", {
    cell: (info) => info.getValue(),
    header: "Client",
    size: 1,
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  // Nested accessors need an explicit id: TanStack turns the dots into
  // underscores, which the responsive collapsing would not match.
  columnHelper.accessor("clientOrganizationReport.numActiveJobs", {
    id: "numActiveJobs",
    cell: (info) => info.getValue(),
    header: "# Jobs",
    size: 1,
    meta: { priority: COLUMN_PRIORITY.IMPORTANT },
  }),
  columnHelper.accessor("clientOrganizationReport.locationsDescription", {
    id: "locationsDescription",
    cell: (info) => info.getValue(),
    header: "Locations",
    meta: { priority: COLUMN_PRIORITY.OPTIONAL },
  }),
];

export const recruitersColumns = (currentUser, refetch) => [
  columnHelper.accessor("firstName", {
    cell: (info) => info.getValue(),
    header: "First Name",
    size: 1,
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("lastName", {
    cell: (info) => info.getValue(),
    header: "Last Name",
    size: 1,
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
  columnHelper.accessor("email", {
    cell: (info) => info.getValue(),
    header: "Email",
    size: 1,
    meta: { priority: COLUMN_PRIORITY.IMPORTANT },
  }),
  // The delete button has to stay reachable at every width. It is a `display`
  // column, not an accessor: there is no `actions` field on a user, so an
  // accessor would make the blank header sortable on `undefined` and put the
  // column in front of the global fuzzy filter, which ranks rows on the string
  // "undefined". Turning both off keeps search and sorting honest.
  columnHelper.display({
    id: "actions",
    cell: ({row}) => {
      if (currentUser?.id === row.original.id) {
        return null;
      }
      return (
        <DeleteRecruiterButton
          user={row.original}
          refetch={refetch}
        />
      );
    },
    header: "",
    size: 1,
    enableGlobalFilter: false,
    enableSorting: false,
    meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
  }),
];

export const candidatesColumns = [
  columnHelper.accessor("firstName", {
    cell: (info) => info.getValue(),
    header: "First Name",
    size: 1,
  }),
  columnHelper.accessor("lastName", {
    cell: (info) => info.getValue(),
    header: "Last Name",
    size: 1,
  }),
  columnHelper.accessor("email", {
    cell: (info) => info.getValue(),
    header: "Email",
    size: 1,
  }),
  columnHelper.accessor("primaryPhone", {
    cell: (info) => info.getValue(),
    header: "Phone",
    size: 1,
  }),
  columnHelper.accessor(
    (item) =>
      !!item?.candidateProfile?.resumeUrl &&
      item?.candidateProfile?.resumeUrl !== "",
    {
      cell: (info) => (info.getValue() ? "Yes" : "No"),
      header: "Has Resume?",
      size: 1,
    }
  ),
  columnHelper.accessor("id", {
    cell: (info) => info.getValue(),
    header: "ID",
    size: 0,
  }),
];
